import { useState, useEffect, useCallback } from 'react';
import Button from '../common/Button';
import Badge from '../common/Badge';

/**
 * Round Robin Simulator Component
 *
 * Allows users to simulate round-robin payouts by excluding legs.
 * Example: 4-leg round-robin with "2's" creates 6 parlays. User can simulate
 * what happens if 1 or 2 legs don't hit.
 */
const RoundRobinSimulator = ({ betslip, isOpen, onClose }) => {
  const [excludedLegs, setExcludedLegs] = useState([]);
  const [simulation, setSimulation] = useState(null);

  // Generate all combinations of size k from array
  const combinations = useCallback((arr, k) => {
    if (k === 0) return [[]];
    if (k > arr.length) return [];
    if (k === arr.length) return [arr];

    const result = [];
    for (let i = 0; i <= arr.length - k; i++) {
      const head = arr[i];
      const tail = arr.slice(i + 1);
      const tailCombos = combinations(tail, k - 1);
      tailCombos.forEach(combo => result.push([head, ...combo]));
    }
    return result;
  }, []);

  // Convert American odds to decimal
  const americanToDecimal = useCallback((americanOdds) => {
    if (americanOdds > 0) {
      return (americanOdds / 100) + 1;
    } else {
      return (100 / Math.abs(americanOdds)) + 1;
    }
  }, []);

  // Calculate parlay odds (decimal)
  const calculateParlayOdds = useCallback((legs) => {
    return legs.reduce((acc, leg) => acc * americanToDecimal(leg.odds), 1);
  }, [americanToDecimal]);

  // Calculate payout for a parlay
  const calculateParlayPayout = useCallback((legs, stake) => {
    const parlayOdds = calculateParlayOdds(legs);
    return stake * parlayOdds;
  }, [calculateParlayOdds]);

  const calculateSimulation = useCallback(() => {
    if (!betslip || betslip.betslip_type !== 'round_robin') {
      return;
    }

    const legsPerParlay = betslip.rr_legs_per_parlay || 2;
    const stakePerCombo = betslip.stake_amount || 0;

    // Filter out excluded legs
    const activeLegIndices = betslip.legs
      .map((_, index) => index)
      .filter(index => !excludedLegs.includes(index));

    const activeLegs = activeLegIndices.map(index => betslip.legs[index]);

    // Generate all possible parlays from active legs
    const parlays = combinations(activeLegs, legsPerParlay);

    // Calculate total payout
    let totalPayout = 0;
    parlays.forEach(parlayLegs => {
      totalPayout += calculateParlayPayout(parlayLegs, stakePerCombo);
    });

    // Calculate total risk
    const totalRisk = parlays.length * stakePerCombo;

    // Calculate profit
    const profit = totalPayout - totalRisk;

    setSimulation({
      activeParlays: parlays.length,
      totalParlays: betslip.rr_num_combinations || 0,
      totalRisk,
      totalPayout,
      profit,
      parlays: parlays.map(parlayLegs => ({
        legs: parlayLegs,
        payout: calculateParlayPayout(parlayLegs, stakePerCombo)
      }))
    });
  }, [betslip, excludedLegs, combinations, calculateParlayPayout]);

  useEffect(() => {
    if (isOpen) {
      calculateSimulation();
    }
  }, [excludedLegs, isOpen, calculateSimulation]);

  const toggleLeg = (legIndex) => {
    if (excludedLegs.includes(legIndex)) {
      setExcludedLegs(excludedLegs.filter(i => i !== legIndex));
    } else {
      setExcludedLegs([...excludedLegs, legIndex]);
    }
  };

  if (!isOpen || !betslip || betslip.betslip_type !== 'round_robin') {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative glass-card p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-text-primary">Round Robin Simulator</h2>
              <p className="text-sm text-text-secondary mt-1">
                {betslip.legs?.length}-Pick {betslip.rr_legs_per_parlay}'s × ${betslip.stake_amount?.toFixed(2)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Instructions */}
          <div className="mb-6 p-4 bg-accent-blue/10 border border-accent-blue/20 rounded-lg">
            <p className="text-sm text-text-primary">
              <strong>Simulate losing legs:</strong> Click on legs below to exclude them from the calculation.
              This shows what your payout would be if those legs don't hit.
            </p>
          </div>

          {/* Legs Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-text-primary mb-3">Select Legs to Exclude</h3>
            <div className="space-y-2">
              {betslip.legs?.map((leg, index) => {
                const isExcluded = excludedLegs.includes(index);
                return (
                  <button
                    key={index}
                    onClick={() => toggleLeg(index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isExcluded
                        ? 'border-accent-red bg-accent-red/10 opacity-60'
                        : 'border-border bg-bg-card hover:border-accent-blue'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={isExcluded ? 'error' : 'info'} size="sm">
                            Leg {index + 1}
                          </Badge>
                          {isExcluded && (
                            <span className="text-xs text-accent-red font-semibold">EXCLUDED</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-text-primary">
                          {leg.player_name || `${leg.away_team_name} @ ${leg.home_team_name}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                          <span className="capitalize">{leg.bet_type}</span>
                          {leg.line_value && (
                            <>
                              <span>•</span>
                              <span>{leg.selection} {leg.line_value}</span>
                            </>
                          )}
                          <span>•</span>
                          <span className="font-semibold">{leg.odds > 0 ? '+' : ''}{leg.odds}</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isExcluded
                            ? 'border-accent-red bg-accent-red'
                            : 'border-border'
                        }`}>
                          {isExcluded && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Simulation Results */}
          {simulation && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card p-4">
                  <p className="text-xs text-text-secondary mb-1">Active Parlays</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {simulation.activeParlays} / {simulation.totalParlays}
                  </p>
                </div>
                <div className="glass-card p-4">
                  <p className="text-xs text-text-secondary mb-1">Total Risk</p>
                  <p className="text-2xl font-bold text-text-primary">
                    ${simulation.totalRisk.toFixed(2)}
                  </p>
                </div>
                <div className="glass-card p-4">
                  <p className="text-xs text-text-secondary mb-1">Total Payout</p>
                  <p className="text-2xl font-bold text-accent-blue">
                    ${simulation.totalPayout.toFixed(2)}
                  </p>
                </div>
                <div className="glass-card p-4">
                  <p className="text-xs text-text-secondary mb-1">Net Profit</p>
                  <p className={`text-2xl font-bold ${
                    simulation.profit > 0 ? 'text-accent-green' :
                    simulation.profit < 0 ? 'text-accent-red' :
                    'text-text-primary'
                  }`}>
                    {simulation.profit > 0 ? '+' : ''}${simulation.profit.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Detailed Parlays */}
              {excludedLegs.length === 0 && simulation.activeParlays <= 10 && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-3">
                    All Parlays ({simulation.activeParlays})
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {simulation.parlays.map((parlay, index) => (
                      <div key={index} className="glass-card p-3 border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="info" size="sm">Parlay {index + 1}</Badge>
                          <span className="text-sm font-semibold text-accent-green">
                            ${parlay.payout.toFixed(2)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {parlay.legs.map((leg, legIndex) => (
                            <div key={legIndex} className="text-xs text-text-secondary flex items-center gap-2">
                              <span className="font-medium text-text-primary">
                                {leg.player_name || `${leg.away_team_name} @ ${leg.home_team_name}`}
                              </span>
                              <span>•</span>
                              <span>{leg.bet_type}</span>
                              {leg.line_value && (
                                <>
                                  <span>•</span>
                                  <span>{leg.selection} {leg.line_value}</span>
                                </>
                              )}
                              <span>•</span>
                              <span className="font-semibold">{leg.odds > 0 ? '+' : ''}{leg.odds}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {excludedLegs.length > 0 && (
                <div className="p-4 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
                  <p className="text-sm text-text-primary">
                    <strong>Simulation:</strong> With {excludedLegs.length} leg{excludedLegs.length > 1 ? 's' : ''} excluded,
                    you would have {simulation.activeParlays} winning parlays out of {simulation.totalParlays} total.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <Button
              variant="secondary"
              onClick={() => setExcludedLegs([])}
              className="flex-1"
              disabled={excludedLegs.length === 0}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={onClose}
              className="flex-1"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoundRobinSimulator;
